import * as React from 'react'
import Alert from '@mui/joy/Alert'
import AspectRatio from '@mui/joy/AspectRatio'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import Divider from '@mui/joy/Divider'
import IconButton from '@mui/joy/IconButton'
import Link from '@mui/joy/Link'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import Sheet from '@mui/joy/Sheet'
import Textarea from '@mui/joy/Textarea'
import Typography from '@mui/joy/Typography'
import BusinessIcon from '@mui/icons-material/Business'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import DownloadIcon from '@mui/icons-material/Download'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
    MC_VERIFICATION_REVIEW_ACTIONS,
    MC_VERIFICATION_DOC_LABELS,
    McVerificationDocReviewStatus,
    McVerificationDocType,
    McVerificationDocView,
    McVerificationEligibilityView,
    McVerificationFmcsaRecordView,
    McVerificationReviewAction,
} from '@numeo/types-core'
import axios from 'axios'

type DecisionState = Record<string, Record<string, { action: McVerificationReviewAction; reason: string }>>

interface PendingReview {
    workspaceId: string
    workspaceProjectName: string
    companyName: string | null
    mcNumber: string | null
    documents: McVerificationDocView[]
    requiredDocumentTypes: McVerificationDocType[]
    fmcsaRecord: McVerificationFmcsaRecordView | null
    usesFactoring?: boolean
    eligibility: McVerificationEligibilityView
}

function decisionKey(type: McVerificationDocType, prefix: 'doc'): string {
    return `${prefix}:${type}`
}

function actionRequiresReason(action: McVerificationReviewAction): boolean {
    return action !== 'approve'
}

function actionLabel(action: McVerificationReviewAction): string {
    switch (action) {
        case 'approve':
            return 'Approve'
        case 'request_resubmission':
            return 'Resubmit'
        case 'require_reverification':
            return 'Reverify'
        case 'reject':
            return 'Reject'
        case 'expire':
            return 'Expire'
    }
}

function statusLabel(status: string): string {
    return status.replace(/_/g, ' ')
}

function fileIcon(fileName: string) {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? ''
    if (ext === 'pdf') return <PictureAsPdfIcon sx={{ fontSize: 20 }} />
    return <InsertDriveFileIcon sx={{ fontSize: 20 }} />
}

function triggerDownload(url: string, fileName: string): void {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.target = '_blank'
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

/** Per-row completion snapshot for the at-a-glance chip in the card header. */
function reviewProgress(row: PendingReview, decisions: DecisionState): { total: number; decided: number } {
    const rowDecisions = decisions[row.workspaceProjectName] || {}
    const keys = row.requiredDocumentTypes.map((t) => decisionKey(t, 'doc'))
    const decided = keys.filter((key) => {
        const decision = rowDecisions[key]
        return decision && (!actionRequiresReason(decision.action) || decision.reason.trim())
    }).length
    return { total: keys.length, decided }
}

export default function McVerificationReview() {
    const [rows, setRows] = React.useState<PendingReview[]>([])
    const [loading, setLoading] = React.useState(true)
    const [actingOn, setActingOn] = React.useState<string | null>(null)
    const [decisions, setDecisions] = React.useState<DecisionState>({})
    const [preview, setPreview] = React.useState<{ url: string; fileName: string } | null>(null)
    const [previewLoading, setPreviewLoading] = React.useState<string | null>(null)
    const [error, setError] = React.useState<string | null>(null)

    const seedDecisions = React.useCallback((reviews: PendingReview[]): DecisionState => {
        const seeded: DecisionState = {}
        for (const row of reviews) {
            seeded[row.workspaceProjectName] = {}
            for (const doc of row.documents) {
                if (doc.reviewStatus === McVerificationDocReviewStatus.APPROVED) {
                    seeded[row.workspaceProjectName][decisionKey(doc.type, 'doc')] = { action: 'approve', reason: '' }
                } else if (
                    doc.reviewStatus === McVerificationDocReviewStatus.REJECTED ||
                    doc.reviewStatus === McVerificationDocReviewStatus.ACTION_REQUIRED ||
                    doc.reviewStatus === McVerificationDocReviewStatus.EXPIRED
                ) {
                    seeded[row.workspaceProjectName][decisionKey(doc.type, 'doc')] = {
                        action:
                            doc.reviewStatus === McVerificationDocReviewStatus.EXPIRED
                                ? 'expire'
                                : doc.reviewStatus === McVerificationDocReviewStatus.ACTION_REQUIRED
                                  ? 'request_resubmission'
                                  : 'reject',
                        reason: doc.reviewReason || '',
                    }
                }
            }
        }
        return seeded
    }, [])

    const mergeDecisions = React.useCallback(
        (
            previous: DecisionState,
            reviews: PendingReview[],
            options?: { preserveUnsavedForOtherRows?: boolean; resetWorkspaceProjectName?: string | null }
        ): DecisionState => {
            const seeded = seedDecisions(reviews)
            if (!options?.preserveUnsavedForOtherRows) return seeded

            const merged: DecisionState = {}
            for (const row of reviews) {
                if (row.workspaceProjectName === options.resetWorkspaceProjectName) {
                    merged[row.workspaceProjectName] = seeded[row.workspaceProjectName] || {}
                    continue
                }
                merged[row.workspaceProjectName] = {
                    ...(seeded[row.workspaceProjectName] || {}),
                    ...(previous[row.workspaceProjectName] || {}),
                }
            }
            return merged
        },
        [seedDecisions]
    )

    const fetchPending = React.useCallback(
        async (options?: { preserveUnsavedForOtherRows?: boolean; resetWorkspaceProjectName?: string | null }) => {
            setLoading(true)
            try {
                const response = await axios.get<{ success: boolean; data: PendingReview[] }>('gluon/mc-verification/pending')
                const data = response.data.data || []
                setRows(data)
                setDecisions((previous) => mergeDecisions(previous, data, options))
                setError(null)
            } catch (e) {
                setError('Failed to load pending reviews. Please refresh.')
                console.error('Error fetching pending MC verifications:', e)
            } finally {
                setLoading(false)
            }
        },
        [mergeDecisions]
    )

    React.useEffect(() => {
        fetchPending()
    }, [fetchPending])

    const openDocument = async (workspaceProjectName: string, type: McVerificationDocType, fileName: string) => {
        const key = `${workspaceProjectName}:${type}`
        try {
            setPreviewLoading(key)
            const response = await axios.get<{ success: boolean; data: { url: string } }>(
                `gluon/mc-verification/${encodeURIComponent(workspaceProjectName)}/documents/${type}/url`
            )
            const url = response.data.data.url
            const ext = fileName.split('.').pop()?.toLowerCase() ?? ''
            if (ext === 'pdf') {
                triggerDownload(url, fileName)
                return
            }
            setPreview({ url, fileName })
        } catch (e) {
            setError('Failed to open the document. Please try again.')
            console.error('Error opening document:', e)
        } finally {
            setPreviewLoading(null)
        }
    }

    const setDecision = (workspaceProjectName: string, key: string, next: Partial<{ action: McVerificationReviewAction; reason: string }>) => {
        setDecisions((prev) => ({
            ...prev,
            [workspaceProjectName]: {
                ...(prev[workspaceProjectName] || {}),
                [key]: { ...(prev[workspaceProjectName]?.[key] || { action: 'approve', reason: '' }), ...next },
            },
        }))
    }

    const canSubmit = (row: PendingReview): boolean => {
        const rowDecisions = decisions[row.workspaceProjectName] || {}
        for (const docType of row.requiredDocumentTypes) {
            const decision = rowDecisions[decisionKey(docType, 'doc')]
            if (!decision) return false
            if (actionRequiresReason(decision.action) && !decision.reason.trim()) return false
        }
        return true
    }

    const submit = async (row: PendingReview) => {
        const rowDecisions = decisions[row.workspaceProjectName] || {}
        const documentDecisions = row.documents
            .filter((doc) => rowDecisions[decisionKey(doc.type, 'doc')])
            .map((doc) => ({
                type: doc.type,
                action: rowDecisions[decisionKey(doc.type, 'doc')].action,
                reason: rowDecisions[decisionKey(doc.type, 'doc')].reason,
            }))

        setActingOn(row.workspaceProjectName)
        setError(null)
        try {
            await axios.post(`gluon/mc-verification/${encodeURIComponent(row.workspaceProjectName)}/review-documents`, {
                documentDecisions,
            })
            // Re-sync from the server after every decision. Rejected / resubmission cases stay in the
            // queue, while truly completed rows disappear naturally once the backend no longer returns them.
            await fetchPending({ preserveUnsavedForOtherRows: true, resetWorkspaceProjectName: row.workspaceProjectName })
        } catch (e) {
            const status = axios.isAxiosError(e) ? e.response?.status : undefined
            const message = (axios.isAxiosError(e) && e.response?.data?.message) || 'Failed to submit review'
            setError(message)
            if (status === 409 || status === 400) {
                void fetchPending({ preserveUnsavedForOtherRows: true, resetWorkspaceProjectName: row.workspaceProjectName })
            }
            console.error('Error reviewing MC verification documents:', e)
        } finally {
            setActingOn(null)
        }
    }

    const renderDecisionControls = (
        workspaceProjectName: string,
        key: string,
        actions: readonly McVerificationReviewAction[] = MC_VERIFICATION_REVIEW_ACTIONS
    ) => {
        const decision = decisions[workspaceProjectName]?.[key]
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {actions.map((action) => (
                        <Button
                            key={action}
                            size="sm"
                            color={action === 'approve' ? 'success' : action === 'reject' ? 'danger' : action === 'expire' ? 'warning' : 'primary'}
                            variant={decision?.action === action ? 'solid' : 'soft'}
                            onClick={() => setDecision(workspaceProjectName, key, { action, reason: action === 'approve' ? '' : (decision?.reason ?? '') })}
                        >
                            {actionLabel(action)}
                        </Button>
                    ))}
                </Box>
                {decision && actionRequiresReason(decision.action) && (
                    <Textarea
                        size="sm"
                        minRows={1}
                        placeholder="Reason shown to the carrier"
                        value={decision.reason}
                        onChange={(event) => setDecision(workspaceProjectName, key, { reason: event.target.value })}
                        color={!decision.reason.trim() ? 'danger' : 'neutral'}
                    />
                )}
            </Box>
        )
    }

    const pendingCount = rows.length
    const readyToSubmitCount = rows.filter((row) => canSubmit(row)).length

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography level="h3">MC Verification Review</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        Review the uploaded evidence for each required document. Final eligibility is based on FMCSA checks plus approval of the required
                        uploaded files.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {!loading && rows.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip size="lg" variant="soft" color="neutral" startDecorator={<LocalShippingIcon sx={{ fontSize: 16 }} />}>
                                {pendingCount} pending
                            </Chip>
                            <Chip
                                size="lg"
                                variant="soft"
                                color={readyToSubmitCount > 0 ? 'success' : 'neutral'}
                                startDecorator={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                            >
                                {readyToSubmitCount} ready to submit
                            </Chip>
                        </Box>
                    )}
                    <Button variant="outlined" color="neutral" size="sm" startDecorator={<RefreshIcon />} onClick={fetchPending} loading={loading}>
                        Refresh
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert color="danger" variant="soft" sx={{ mb: 2 }} endDecorator={<ModalClose onClick={() => setError(null)} sx={{ position: 'static' }} />}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                    <CircularProgress size="sm" />
                </Box>
            ) : rows.length === 0 ? (
                <Sheet variant="soft" color="success" sx={{ borderRadius: 'md', p: 6, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography level="title-sm">All caught up</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        No pending verifications right now.
                    </Typography>
                </Sheet>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {rows.map((row) => {
                        const progress = reviewProgress(row, decisions)
                        const rowReady = canSubmit(row)
                        const oosStatus = row.eligibility.notOutOfService
                        const oosColor = oosStatus === null ? 'warning' : oosStatus ? 'success' : 'danger'
                        const oosLabel = oosStatus === null ? 'OOS unknown' : oosStatus ? 'not OOS' : 'out of service'
                        return (
                            <Sheet
                                key={row.workspaceId}
                                variant="outlined"
                                sx={{
                                    borderRadius: 'lg',
                                    overflow: 'hidden',
                                    borderColor: rowReady ? 'success.300' : 'divider',
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, p: 2.5, pb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 40,
                                                height: 40,
                                                borderRadius: 'md',
                                                bgcolor: 'background.level1',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                        </Box>
                                        <Box>
                                            <Typography level="title-md">{row.companyName || row.workspaceProjectName}</Typography>
                                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                                {row.workspaceProjectName} · {row.workspaceId} · MC {row.mcNumber || '—'}
                                            </Typography>
                                            {row.fmcsaRecord && (
                                                <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.25 }}>
                                                    FMCSA · DOT {row.fmcsaRecord.dotNumber}
                                                    {row.fmcsaRecord.legalName ? ` · ${row.fmcsaRecord.legalName}` : ''}
                                                    {row.fmcsaRecord.city ? ` · ${row.fmcsaRecord.city}, ${row.fmcsaRecord.state ?? ''}` : ''}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            <Chip size="sm" variant="soft" color={row.eligibility.fmcsaAuthorityActive ? 'success' : 'warning'}>
                                                authority {row.eligibility.fmcsaAuthorityActive ? 'active' : 'blocked'}
                                            </Chip>
                                            <Chip size="sm" variant="soft" color={oosColor}>
                                                {oosLabel}
                                            </Chip>
                                        </Box>
                                        <Chip
                                            size="sm"
                                            variant="soft"
                                            color={rowReady ? 'success' : 'neutral'}
                                            startDecorator={rowReady ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <HourglassEmptyIcon sx={{ fontSize: 14 }} />}
                                        >
                                            {progress.decided}/{progress.total} decided
                                        </Chip>
                                    </Box>
                                </Box>

                                <Divider />

                                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    <Box>
                                        <Typography level="title-sm" sx={{ mb: 1 }}>
                                            Uploaded evidence
                                        </Typography>
                                        <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.25 }}>
                                            Approve these if the uploaded file is acceptable and current. Required documents are COI, plus NOA when the carrier
                                            uses factoring.
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {row.documents.map((doc) => {
                                                const previewKey = `${row.workspaceProjectName}:${doc.type}`
                                                return (
                                                    <Box
                                                        key={doc.type}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 'sm',
                                                            p: 1.5,
                                                            display: 'flex',
                                                            gap: 1.5,
                                                        }}
                                                    >
                                                        <AspectRatio
                                                            ratio="1"
                                                            sx={{ width: 44, borderRadius: 'sm', flexShrink: 0, bgcolor: 'background.level1' }}
                                                        >
                                                            <Box
                                                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.tertiary' }}
                                                            >
                                                                {fileIcon(doc.fileName)}
                                                            </Box>
                                                        </AspectRatio>
                                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                                                <Box>
                                                                    <Typography level="body-sm" fontWeight="lg">
                                                                        {MC_VERIFICATION_DOC_LABELS[doc.type] ?? doc.type}
                                                                        {row.requiredDocumentTypes.includes(doc.type) ? '' : ' (optional)'}
                                                                    </Typography>
                                                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                                                        File review status: {statusLabel(doc.reviewStatus)}
                                                                    </Typography>
                                                                    <Link
                                                                        level="body-xs"
                                                                        component="button"
                                                                        startDecorator={
                                                                            previewLoading === previewKey ? (
                                                                                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} />
                                                                            ) : (
                                                                                <VisibilityIcon sx={{ fontSize: 14 }} />
                                                                            )
                                                                        }
                                                                        disabled={previewLoading === previewKey}
                                                                        onClick={() => openDocument(row.workspaceProjectName, doc.type, doc.fileName)}
                                                                    >
                                                                        {doc.fileName}
                                                                    </Link>
                                                                </Box>
                                                                <Chip size="sm" variant="soft">
                                                                    {statusLabel(doc.reviewStatus)}
                                                                </Chip>
                                                            </Box>
                                                            {renderDecisionControls(row.workspaceProjectName, decisionKey(doc.type, 'doc'))}
                                                        </Box>
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                    </Box>
                                </Box>

                                <Divider />

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'background.level1' }}>
                                    {!rowReady && (
                                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                            Decide every required document to submit.
                                        </Typography>
                                    )}
                                    <Button color="primary" loading={actingOn === row.workspaceProjectName} disabled={!rowReady} onClick={() => submit(row)}>
                                        Submit review
                                    </Button>
                                </Box>
                            </Sheet>
                        )
                    })}
                </Box>
            )}

            <Modal open={Boolean(preview)} onClose={() => setPreview(null)}>
                <ModalDialog layout="center" sx={{ width: 'min(900px, 92vw)', maxHeight: '92vh', p: 1.5 }}>
                    <ModalClose />
                    {preview &&
                        (() => {
                            const ext = preview.fileName.split('.').pop()?.toLowerCase() ?? ''
                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 4 }}>
                                        <Typography level="title-sm" noWrap>
                                            {preview.fileName}
                                        </Typography>
                                        <IconButton
                                            size="sm"
                                            variant="plain"
                                            component="a"
                                            href={preview.url}
                                            target="_blank"
                                            rel="noopener"
                                            sx={{ ml: 'auto' }}
                                        >
                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton size="sm" variant="plain" component="a" href={preview.url} download={preview.fileName} sx={{ ml: 0.5 }}>
                                            <DownloadIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                    {isImage ? (
                                        <Box
                                            component="img"
                                            src={preview.url}
                                            alt={preview.fileName}
                                            sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 'sm', alignSelf: 'center' }}
                                        />
                                    ) : (
                                        <Typography level="body-sm" sx={{ py: 4, textAlign: 'center' }}>
                                            Preview not available for this file type.{' '}
                                            <Link href={preview.url} target="_blank" rel="noopener">
                                                Download
                                            </Link>
                                        </Typography>
                                    )}
                                </Box>
                            )
                        })()}
                </ModalDialog>
            </Modal>
        </Box>
    )
}
